import * as React from 'react';
import styled from 'styled-components';

import { Loader } from '../atoms';
import { getPermitInspections } from '../api/inspections';
import { formatDateTime } from '../utils/formatDate';
import { DEFAULT_DATE_FORMAT, MONTH_FULL_LIST } from '../constants';

const pad2 = (n: number) => String(n).padStart(2, '0');

const monthRange = (month: number, year: number) => {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    dateFrom: `${year}-${pad2(month)}-01`,
    dateTo: `${year}-${pad2(month)}-${pad2(lastDay)}`,
  };
};

const fmtDate = (d?: string | null): string =>
  d ? formatDateTime(d, DEFAULT_DATE_FORMAT) || '—' : '—';

const periodLabel = (period?: number): string | null => {
  if (period === 0) return 'AM';
  if (period === 1) return 'PM';
  return null;
};

const joinName = (...parts: Array<string | undefined>) =>
  parts.filter(Boolean).join(' ');

// `lastComment` comes back either as a plain string or as a comment object.
const commentText = (comment?: string | { text?: string } | null): string => {
  if (!comment) return '';
  if (typeof comment === 'string') return comment;
  return comment.text || '';
};

const asText = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number') return String(value) || '—';
  return '—';
};

export interface CompletedInspection {
  id: number;
  projectName?: string;
  permitName?: string;
  permitNumber?: string;
  projectAddress?: string;
  projectJurisdiction?: string;
  activityTypeName?: string;
  inspectionDate?: string | null;
  period?: number;
  managerFirstName?: string;
  managerLastName?: string;
  contactFirstName?: string;
  contactLastName?: string;
  phoneNumber?: string;
  lastComment?: string | { text?: string } | null;
  permit?: number;
  projectId?: number;
}

interface Props {
  inspectorId?: number | null;
  month: number | null;
  year: number | null;
  onSelectInspection: (inspection: CompletedInspection) => void;
}

const CompletedInspectionsList = ({
  inspectorId,
  month,
  year,
  onSelectInspection,
}: Props) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [items, setItems] = React.useState<CompletedInspection[]>([]);

  React.useEffect(() => {
    if (!inspectorId || !month || !year) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError(false);
    const { dateFrom, dateTo } = monthRange(month, year);
    getPermitInspections({
      inspector: inspectorId,
      isCompleted: true,
      ordering: '-is_emergency,follow_up',
      graph: true,
      dateFrom,
      dateTo,
      limit: 100000000,
      offset: 0,
    })
      .then(response => {
        if (!active) return;
        const results = (response as any)?.data?.results;
        setItems(Array.isArray(results) ? results : []);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [inspectorId, month, year]);

  const monthLabel =
    month && year ? `${MONTH_FULL_LIST[month - 1] || ''} ${year}` : '';

  if (loading) {
    return (
      <Centered>
        <Loader />
      </Centered>
    );
  }

  if (error) {
    return <EmptyState>Unable to load inspections. Please try again.</EmptyState>;
  }

  if (!items.length) {
    return (
      <EmptyState>
        No completed inspections{monthLabel ? ` for ${monthLabel}` : ''}.
      </EmptyState>
    );
  }

  return (
    <Wrapper>
      <SummaryBar>
        <SummaryItem>
          <SummaryLabel>Completed</SummaryLabel>
          <SummaryValue>{items.length}</SummaryValue>
        </SummaryItem>
        {monthLabel && (
          <SummaryItem>
            <SummaryLabel>Period</SummaryLabel>
            <SummaryValue>{monthLabel}</SummaryValue>
          </SummaryItem>
        )}
      </SummaryBar>

      <List>
        {items.map(it => {
          const title =
            joinName(it.projectName, it.permitName ? `| ${it.permitName}` : undefined) ||
            'Inspection';
          const period = periodLabel(it.period);
          const manager = joinName(it.managerFirstName, it.managerLastName);
          const contact = joinName(it.contactFirstName, it.contactLastName);
          const comment = commentText(it.lastComment);
          return (
            <Card
              key={it.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectInspection(it)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectInspection(it);
                }
              }}
            >
              <CardHeader>
                <CardTitle>{title}</CardTitle>
                {period && <Chip>{period}</Chip>}
              </CardHeader>
              <FieldGrid>
                <Field>
                  <FieldLabel>Inspection ID</FieldLabel>
                  <FieldValue>{it.id}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Inspection Type</FieldLabel>
                  <FieldValue>{asText(it.activityTypeName)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Inspection Date</FieldLabel>
                  <FieldValue>{fmtDate(it.inspectionDate)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Permit Number</FieldLabel>
                  <FieldValue>{asText(it.permitNumber)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Jurisdiction</FieldLabel>
                  <FieldValue>{asText(it.projectJurisdiction)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Project Manager</FieldLabel>
                  <FieldValue>{manager || '—'}</FieldValue>
                </Field>
                <Field $wide>
                  <FieldLabel>Project Address</FieldLabel>
                  <FieldValue>{asText(it.projectAddress)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Contact</FieldLabel>
                  <FieldValue>{contact || '—'}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Contact Phone</FieldLabel>
                  <FieldValue>{asText(it.phoneNumber)}</FieldValue>
                </Field>
              </FieldGrid>
              {comment && (
                <CommentRow>
                  <FieldLabel>Last Comment</FieldLabel>
                  <CommentText
                    // comment text is rich HTML from the editor (same as InspectionsTable)
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: comment }}
                  />
                </CommentRow>
              )}
            </Card>
          );
        })}
      </List>
    </Wrapper>
  );
};

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

const Wrapper = styled.div`
  font-family: ${FONT_STACK};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const SummaryBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const SummaryItem = styled.div`
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
`;

const SummaryLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 60vh;
  overflow-y: auto;
`;

const Card = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  box-shadow: var(--shadow-xs);
  padding: 16px 18px;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--primary-400);
    box-shadow: var(--shadow-focus, 0 0 0 3px rgba(0, 0, 0, 0.06));
  }
  &:focus-visible {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus, 0 0 0 3px rgba(0, 0, 0, 0.06));
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--border);
`;

const CardTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  word-break: break-word;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px 24px;
`;

const Field = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  ${({ $wide }) => $wide && 'grid-column: 1 / -1;'}
`;

const FieldLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const FieldValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--mainText);
  word-break: break-word;
`;

const CommentRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
`;

const CommentText = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-700);
  word-break: break-word;

  p {
    margin: 0 0 6px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 0 0 6px;
    padding-left: 18px;
  }
  a {
    color: var(--primary-600);
  }
`;

const Chip = styled.span`
  flex-shrink: 0;
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100);
`;

export default CompletedInspectionsList;
